import { useState, useCallback } from 'react';
import API_CONFIG from '../../../config/api';

/**
 * POST /pkb/{projectId}/analyze-requirement — DB-backed requirement analysis.
 */
export default function useRequirementAnalysis({ projectId }) {
  const [loading, setLoading] = useState(false);

  const analyzeRequirement = useCallback(
    async (knowledgeId, { forceRefresh = false } = {}) => {
      if (!projectId) {
        throw new Error('No project selected.');
      }
      setLoading(true);
      try {
        const res = await API_CONFIG.request(API_CONFIG.ENDPOINTS.PKB_ANALYZE_REQUIREMENT(projectId), {
          method: 'POST',
          body: JSON.stringify({
            knowledge_id: knowledgeId,
            force_refresh: Boolean(forceRefresh),
          }),
        });
        let json = {};
        try {
          json = await res.json();
        } catch {
          json = {};
        }

        if (res.status === 504 || json.status === 'timeout') {
          const err = new Error(
            json.message || 'Requirement analysis took longer than expected. Please try again.',
          );
          err.code = 'timeout';
          throw err;
        }

        if (!res.ok || json.status === 'error') {
          const err = new Error(
            json.message || json.detail || `Requirement analysis failed (${res.status}).`,
          );
          err.code = json.status || 'error';
          throw err;
        }

        if (json.status !== 'success') {
          const err = new Error(json.message || 'Unexpected response from requirement analysis.');
          err.code = 'unexpected';
          throw err;
        }

        return json;
      } finally {
        setLoading(false);
      }
    },
    [projectId],
  );

  return { analyzeRequirement, loading };
}
